<template>
  <div>
    <page-title>Survey Entries</page-title>
    <loading v-if="loading" />
    <main v-else>
      <tabs v-if="formattedVersions">
        <tabs-item
          :key="index"
          v-for="(version, index) in formattedVersions"
          @click="filterResults(version.id)"
          :active="currentVersion === version.id"
          >Version {{ version.version }}</tabs-item
        >
      </tabs>
      <section-block>
        <template slot="header">
          <base-button @click="gotoSurveys">
            <icon name="back" /> Back to surveys
          </base-button>
          <base-button @click="generateCsv">Generate CSV</base-button>
        </template>
        <h1>{{ currentSurvey.current_version.title }}</h1>

        <scrollable-table>
          <template slot="header">
            <tr>
              <th>Date Submitted</th>
              <th v-if="!currentSurvey.anonymous">Submitted by</th>
              <th v-for="title in surveyEntryTitles" :key="title.id">
                {{ title.value }}
              </th>
            </tr>
          </template>
          <template slot="body">
            <tr v-for="(row, index) in filteredResults" :key="index">
              <td>
                {{ $dayjs(row.dateSubmitted).format("DD/MM/YYYY HH:mm") }}
              </td>
              <td v-if="!currentSurvey.anonymous">
                {{ row.user ? row.user.name : "N/A" }}
                <span v-if="row.user && row.user.email"><br />(<a :href="`mailto:${row.user.email}`">{{row.user.email}}</a>)</span>
              </td>
              <td v-for="answer in row.answers" :key="answer.id">
                <template v-if="answer.isFile">
                  <ol class="listless" v-if="answer.answer != 'N/A'">
                    <li
                      v-for="(file, index) in `${answer.answer}`.split(',')"
                      :key="index"
                    >
                      <base-button as-link @click="downloadFile(file)"
                        >View file {{ index + 1 }}<br
                      /></base-button>
                    </li>
                  </ol>
                  <span v-else>N/A</span>
                </template>
                <template v-else>
                  {{ answer.answer }}
                </template>
              </td>
            </tr>
          </template>
        </scrollable-table>
      </section-block>
    </main>
  </div>
</template>

<script>
import filesApi from "@/api/files";
import ScrollableTable from "@/components/ScrollableTable.vue";
import { mapGetters, mapState } from "vuex";
export default {
  name: "AdminFormEntriesPage",
  components: {
    ScrollableTable,
  },
  props: {},
  data() {
    return {
      loading: true,
      currentVersion: null,
      currentSurveyElements: null,
    };
  },
  computed: {
    ...mapGetters("Surveys", ["formattedSubmissions", "formattedVersions"]),
    ...mapState("Surveys", ["currentSurvey", "surveySubmissions", "surveyVersions"]),
    surveyEntryTitles() {
      const results = this.filteredResults;
      const questions = [];
      const ids = [];

      // have to go through each answer
      results.forEach((res) => {
        res.answers.forEach((ans) => {
          const obj = {
            id: ans.id,
            value: ans.question,
          };
          if (!ids.includes(ans.origId)) {
            questions.push(obj);
            ids.push(ans.origId);
          }
        });
      });

      return questions.sort((a, b) => a.id - b.id);
    },
    filteredResults() {
      const versionResults = this.surveySubmissions.filter(
        (res) => res.version_id === this.currentVersion
      );

      return versionResults.map((res) => {
        const toReturn = {
          user_id: res.user_id,
          dateSubmitted: res.created_time,
          answers: [],
          user: !this.currentSurvey.anonymous && res.user ? res.user : null,
        };

        const data = JSON.parse(res.data);

        Object.keys(data).forEach((answer) => {
          let answerText;
          try {
            answerText = JSON.parse(data[answer]);
          } catch (err) {
            answerText = data[answer];
          }
          if (typeof answerText === "object") {
            const answerSet = answerText.map((ans) => ans.value || ans);
            answerText = answerSet.toString();
          }
          if (typeof answerText === "array") {
            answerText = answerText.toString();
          }
          let getElement = this.currentSurveyElements.filter(
            (elem) => elem.id == answer.split("_")[1]
          );

          let fieldType = getElement[0].type;

          if (getElement[0].type == "base-row") {
            // if we are a row we need to try and get the specific element
            const leftColData = getElement[0].properties.filter(
              (prop) => prop.key == "leftColumn"
            );
            const rightColData = getElement[0].properties.filter(
              (prop) => prop.key == "rightColumn"
            );
            const leftColParse = JSON.parse(leftColData[0].value);
            const rightColParse = JSON.parse(rightColData[0].value);

            if (
              (leftColParse.properties.label &&
                leftColParse.properties.label == answer.split("_")[0]) ||
              (leftColParse.properties.title &&
                leftColParse.properties.title == answer.split("_")[0])
            ) {
              fieldType = leftColParse.type;
              answer = `${answer}-1`;
            }
            if (
              (rightColParse.properties.label &&
                rightColParse.properties.label == answer.split("_")[0]) ||
              (rightColParse.properties.title &&
                rightColParse.properties.title == answer.split("_")[0])
            ) {
              fieldType = rightColParse.type;
              answer = `${answer}-2`;
            }
          }

          toReturn.answers.push({
            id: answer.split("_")[1],
            question: answer.split("_")[0],
            origId: answer,
            answer: answerText,
            isFile:
              getElement && getElement.length == 1
                ? ["media-file", "media-file-upload"].includes(fieldType)
                : false,
          });
        });

        toReturn.answers = toReturn.answers.sort(
          (a, b) => a.id.replace("-", "") - b.id.replace("-", "")
        );

        return toReturn;
      });
    },
  },
  watch: {},

  beforeCreate() {},
  async created() {
    // reset the form
    this.$store.dispatch("Surveys/reset");
    this.$store.commit("Surveys/SET_SURVEY_ENTRIES", []);

    // get details of the form
    await this.$store.dispatch("Surveys/read", { surveyId: this.$route.params.id });
    await this.$store.dispatch("Surveys/listVersions", this.$route.params.id);

    // get the form entries
    await this.$store.dispatch("Surveys/getEntries", {
      surveyId: this.$route.params.id,
    });

    this.currentVersion = this.formattedVersions[0].id;

    this.currentSurveyElements = this.currentSurvey.current_version.elements;

    this.loading = false;
  },
  beforeMount() {},
  mounted() {},
  beforeUpdate() {},
  updated() {},
  methods: {
    downloadFile(fileId) {
      filesApi.read(fileId).then((file) => {
        this.$store.dispatch("files/download", file);
      });
    },

    async filterResults(ver) {
      this.loading = true;
      await this.$store.dispatch("Surveys/getVersion", {
        surveyId: this.currentSurvey.id,
        versionId: ver,
      });
      this.currentSurveyElements = this.$store.state.Surveys.current_version.elements;
      this.currentVersion = ver;
      this.loading = false;
      // need to load a different version of the form
    },
    gotoForms() {
      this.$router.push({
        name: "AdminFormIndexPage",
      });
    },
    generateCsv() {
      this.$store
        .dispatch("Surveys/generateCsv", this.filteredResults)
        .then((resp) => {
          var blob = new Blob([resp]);
          if (window.navigator.msSaveOrOpenBlob)
            window.navigator.msSaveBlob(
              blob,
              `formResults_${this.currentSurvey.current_version.title}.csv`
            );
          else {
            var a = window.document.createElement("a");
            a.href = window.URL.createObjectURL(blob, { type: "text/plain" });
            a.download = `formResults_${this.currentSurvey.current_version.title}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          }
        });
    },
  },
};
</script>

<style lang="scss" scoped></style>
